import { Fragment, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Modal } from '../components/Modal.js';
import { Icon } from '../components/Icon.js';
import { api, type CardPreview as CardPreviewData } from '../lib/api.js';
import { teamName } from './wording.js';

/**
 * "See the Teams card" (F10 P3): the current stage's card exactly as the API
 * builds it, drawn by a small read-only Adaptive Card renderer. Covers the
 * elements JFH Forms' cards use: Container, TextBlock, ColumnSet and the
 * Input.Text / Input.ChoiceSet inputs.
 */

type El = Record<string, unknown> & { type?: string };

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** TextBlock's markdown subset that the cards use: **bold**. */
function inline(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : <Fragment key={i}>{part}</Fragment>,
  );
}

const SIZE: Record<string, number> = { Small: 12, Default: 13.5, Medium: 15, Large: 18 };
const COLOUR: Record<string, string> = { Accent: 'var(--accent-text)', Attention: 'var(--st-declined)', Good: 'var(--accent-text)' };
const CONTAINER: Record<string, CSSProperties> = {
  accent: { background: 'var(--accent-soft)' },
  emphasis: { background: 'var(--bg-sunken)' },
  warning: { background: 'var(--warning-100)' },
};

function gap(el: El): number {
  if (el.spacing === 'None') return 0;
  if (el.spacing === 'Small') return 4;
  if (el.spacing === 'Medium') return 12;
  if (el.spacing === 'Large') return 18;
  return 8;
}

function Element({ el }: { el: El }) {
  const top: CSSProperties = {
    marginTop: gap(el),
    ...(el.separator ? { borderTop: '1px solid var(--border)', paddingTop: gap(el) } : {}),
  };
  switch (el.type) {
    case 'Container':
      return (
        <div
          style={{
            ...top,
            ...(CONTAINER[str(el.style)] ?? {}),
            padding: el.style ? (el.bleed ? '12px 14px' : '10px 12px') : 0,
            margin: el.bleed ? `${gap(el)}px -14px 0` : undefined,
            borderRadius: el.bleed ? 0 : 6,
          }}
        >
          <Elements items={el.items} />
        </div>
      );
    case 'ColumnSet':
      return (
        <div style={{ ...top, display: 'flex', gap: 12 }}>
          {(Array.isArray(el.columns) ? (el.columns as El[]) : []).map((c, i) => (
            <div key={i} style={{ flex: 1, minWidth: 0 }}>
              <Elements items={c.items} />
            </div>
          ))}
        </div>
      );
    case 'TextBlock':
      return (
        <div
          style={{
            ...top,
            fontSize: SIZE[str(el.size)] ?? 13.5,
            fontWeight: el.weight === 'Bolder' ? 700 : 400,
            color: COLOUR[str(el.color)] ?? (el.isSubtle ? 'var(--text-3)' : 'var(--text-1)'),
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {inline(str(el.text))}
        </div>
      );
    case 'Input.Text':
    case 'Input.ChoiceSet': {
      const choices = Array.isArray(el.choices) ? (el.choices as { title: string; value: string }[]) : [];
      const shown = el.type === 'Input.ChoiceSet' ? (choices.find((c) => c.value === el.value)?.title ?? '') : str(el.value);
      return (
        <div style={top}>
          {Boolean(el.label) && (
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 3 }}>
              {str(el.label)}
              {el.isRequired === true && <span style={{ color: 'var(--st-declined)' }}> *</span>}
            </div>
          )}
          <div
            style={{
              border: '1px solid var(--border-strong, var(--border))',
              borderRadius: 4,
              padding: '6px 8px',
              minHeight: el.isMultiline ? 48 : 30,
              boxSizing: 'border-box',
              fontSize: 13,
              color: shown ? 'var(--text-1)' : 'var(--text-3)',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span>{shown || str(el.placeholder) || ' '}</span>
            {el.type === 'Input.ChoiceSet' && <Icon name="chevron" size={13} style={{ color: 'var(--text-3)' }} />}
          </div>
        </div>
      );
    }
    default:
      return null;
  }
}

function Elements({ items }: { items: unknown }) {
  if (!Array.isArray(items)) return null;
  return (
    <>
      {(items as El[]).map((el, i) => (
        <Element key={i} el={el} />
      ))}
    </>
  );
}

/** Just the card (exported for tests and the prototype). */
export function AdaptiveCardView({ body }: { body: unknown[] }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0 14px 14px', overflow: 'hidden' }}>
      <Elements items={body} />
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <span className="btn sm" aria-hidden style={{ pointerEvents: 'none' }}>
          Approve
        </span>
        <span className="btn sm" aria-hidden style={{ pointerEvents: 'none' }}>
          Decline
        </span>
        <span className="btn sm ghost" aria-hidden style={{ pointerEvents: 'none' }}>
          Open in JFH Forms
        </span>
      </div>
    </div>
  );
}

export function CardPreviewModal({ id, open, onClose }: { id: string; open: boolean; onClose: () => void }) {
  const [card, setCard] = useState<CardPreviewData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCard(null);
    setError(null);
    api
      .getCardPreview(id)
      .then(setCard)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Couldn’t build the card'));
  }, [id, open]);

  return (
    <Modal open={open} onClose={onClose} width={520}>
      <div style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>The Teams card</h3>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 2 }}>
              {card
                ? `What ${teamName(card.stage)} see in the ${card.channel} channel. Pressing a button there does the same as here.`
                : 'What the approvers see in Teams.'}
            </div>
          </div>
          <button className="icon-btn" aria-label="Close" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
        {error ? (
          <div style={{ fontSize: 13, color: 'var(--st-declined)' }}>{error}</div>
        ) : card ? (
          <AdaptiveCardView body={card.body} />
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Building the card…</div>
        )}
      </div>
    </Modal>
  );
}
